import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styles from './AnimationWrapperEaseInOut.module.css';

interface AnimationWrapperEaseInOutProps {
    children: ReactNode;
    showContent: boolean;
    animatedProperty?: 'height';
    animationDuration?: number;
    animateOnFirstRender?: boolean;
}

const AnimationWrapperEaseInOut = ({
    children,
    showContent,
    animatedProperty = 'height',
    animationDuration = 300,
    animateOnFirstRender = false,
}: AnimationWrapperEaseInOutProps) => {
    const [renderContent, setRenderContent] = useState(showContent);
    const [wrapperStyle, setWrapperStyle] = useState({});

    const timeoutRef = useRef(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        updateWrapperStyle();
    }, []);

    useEffect(() => {
        const wrapper = wrapperRef.current;

        if (!wrapper) {
            if (showContent) {
                setRenderContent(true);
            } else {
                setRenderContent(false);
            }

            return;
        }

        if (showContent) {
            switch (animatedProperty) {
                case 'height':
                    animateHeight(wrapper, 'in');
                    break;
            }
        } else {
            switch (animatedProperty) {
                case 'height':
                    animateHeight(wrapper, 'out');
                    break;
            }
        }

        return () => {
            clearTimeout(timeoutRef.current);
        }
    }, [wrapperRef.current, showContent]);

    const updateWrapperStyle = () => {
        const style = {};
        let height = '0px';

        switch (animatedProperty) {
            case 'height':
                if (!animateOnFirstRender) {
                    height = 'auto';
                }

                style['height'] = height;
                break;
        }

        style['--animation-duration'] = `${animationDuration}ms`;

        setWrapperStyle(style);
    }

    const animateHeight = (wrapper: HTMLDivElement, direction: 'in' | 'out') => {
        if (direction === 'in') {
            wrapper.style.height = '0px';
            wrapper.style.opacity = '0';

            void wrapper.offsetHeight;

            wrapper.style.height = `${wrapper.scrollHeight}px`;
            wrapper.style.opacity = '1';

            timeoutRef.current = setTimeout(() => {
                wrapper.style.height = 'auto';
            }, animationDuration);
        }

        if (direction === 'out') {
            wrapper.style.height = `${wrapper.scrollHeight}px`;
            wrapper.style.opacity = '1';

            void wrapper.offsetHeight;

            wrapper.style.height = '0px';
            wrapper.style.opacity = '0';

            timeoutRef.current = setTimeout(() => {
                setRenderContent(false);
            }, animationDuration);
        }
    }

    if (!renderContent) {
        return null;
    }

    return (
        <div
            ref={wrapperRef}
            className={styles.wrapper}
            style={wrapperStyle}
        >
            {children}
        </div>
    );
};

export default AnimationWrapperEaseInOut;
